import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { AlertProvider } from './contexts/AlertContext';
import { PracticeProvider } from './contexts/PracticeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import UpdateNotification from './components/UpdateNotification';
import LandingPage from './pages/LandingPage';
import DownloadPage from './pages/DownloadPage';
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import ReportsPage from './pages/ReportsPage';
import WallboardPage from './pages/WallboardPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import LicensePage from './pages/LicensePage';
import MessagingPage from './pages/MessagingPage';
import MobileAppPage from './pages/MobileAppPage';
import './App.css';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <PrivateRoute>
          <PracticeProvider>
            <SocketProvider>
              <AlertProvider>
                <DashboardPage />
              </AlertProvider>
            </SocketProvider>
          </PracticeProvider>
        </PrivateRoute>
      } />
      <Route path="/admin" element={
        <PrivateRoute>
          <PracticeProvider>
            <AdminPage />
          </PracticeProvider>
        </PrivateRoute>
      } />
      <Route path="/reports" element={
        <PrivateRoute>
          <PracticeProvider>
            <ReportsPage />
          </PracticeProvider>
        </PrivateRoute>
      } />
      <Route path="/wallboard" element={
        <PrivateRoute>
          <SocketProvider>
            <WallboardPage />
          </SocketProvider>
        </PrivateRoute>
      } />
      <Route path="/payment-success" element={
        <PrivateRoute>
          <PaymentSuccessPage />
        </PrivateRoute>
      } />
      <Route path="/license" element={
        <PrivateRoute>
          <PracticeProvider>
            <LicensePage />
          </PracticeProvider>
        </PrivateRoute>
      } />
      <Route path="/messaging" element={
        <PrivateRoute>
          <PracticeProvider>
            <SocketProvider>
              <MessagingPage />
            </SocketProvider>
          </PracticeProvider>
        </PrivateRoute>
      } />
      <Route path="/mobile" element={<MobileAppPage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  const isElectron = window.electronAPI?.isElectron;
  const Router = isElectron ? HashRouter : BrowserRouter;

  // Hide Emergent badge in desktop app
  useEffect(() => {
    if (isElectron) {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.style.display = 'none';
    }
  }, [isElectron]);

  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <AppRoutes />
          <Toaster position="top-right" />
          <UpdateNotification />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;